import React, { useEffect, useState } from 'react';
import './AgentPage.css';
import Footer from '../../components/Footer/Footer';
import GlobalHeader from '../../components/Common/GlobalHeader';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { pricingProducts } from '../../data/agents';
import Features from '../../components/Features/Features';
import Trust from '../../components/Trust/Trust';
import Separator from '../../components/Separator/Separator';
import { useAuth } from '../../context/AuthContext';
import { useGlobalError } from '../../context/GlobalErrorContext';
import { createApiClient } from '../../lib/api-client';

import { useRegistry } from '../../hooks/useRegistry';

const AgentPage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const { addError } = useGlobalError();
  const { packages, providers, loading: loadingRegistry } = useRegistry();
  const [setupStep, setSetupStep] = useState(1);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [selectedApiMode, setSelectedApiMode] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [loadingDeployments, setLoadingDeployments] = useState(true);
  const [isDeploying, setIsDeploying] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');

  // Merge registry packages with static UI assets (icons)
  const enrichedPackages = packages.map(pkg => {
    const staticData = pricingProducts.find(p => p.id === pkg.slug);
    return {
      ...pkg,
      icon: staticData?.icon,
      price: staticData?.price || '$49', // Fallback or fetch from DB if needed
      period: staticData?.period || '/ mo'
    };
  });

  const selectedProvider = providers.find(p => p.id === selectedProviderId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [agentId]);

  useEffect(() => {
    const fetchDeployments = async () => {
      try {
        const api = createApiClient(getAccessToken);
        const result = await api.get('/deployments');
        setDeployments(result.deployments || []);
      } catch (err) {
        console.error('Failed to fetch deployments:', err);
      } finally {
        setLoadingDeployments(false);
      }
    };
    fetchDeployments();
  }, [getAccessToken]);

  const product = enrichedPackages.find(p => p.slug === agentId);

  const handleDeploy = async () => {
    if (!selectedProviderId || !product) return;

    if (loadingDeployments) return; 

    const isAlreadyDeployed = deployments.some(d => 
      (d.agentSlug === product.slug) && 
      d.status !== 'deleted'
    );

    if (isAlreadyDeployed) {
      addError('This agent is already deployed. Please manage it from your dashboard.', { type: 'info', timeout: 5000 });
      navigate('/dashboard');
      return;
    }

    setIsDeploying(true);

    const api = createApiClient(getAccessToken);
    
    const apiMode = selectedProvider?.isReadyApi ? 'ready_api' : 'own_key';

    const payload = {
      name: `${product.name} Deployment`,
      template: product.slug,
      providerId: selectedProviderId,
      mode: apiMode,
    };

    try {
      const result = await api.post('/deployments', payload);

      if (result.deployment?.id) {
        addError('Deployment created successfully!', { type: 'success', timeout: 3000 });
        navigate('/dashboard');
      } else if (result.error) {
        addError(result.error, { type: 'error' });
        setIsDeploying(false);
      }
    } catch (err) {
      console.error('[AgentPage] Deployment failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Deployment failed';
      addError(errorMessage, { type: 'error' });
      setIsDeploying(false);
    }
  };

  useEffect(() => {
    const handleAutoDeploy = () => {
      handleDeploy();
    };
    window.addEventListener('trigger-readyapi-deploy', handleAutoDeploy);
    return () => window.removeEventListener('trigger-readyapi-deploy', handleAutoDeploy);
  }, [selectedProviderId, handleDeploy]);

  // Render All Products Listing if no productId is provided
  if (!agentId) {
    return (
      <div className="agent-page catalog">
        <GlobalHeader />

        <section className="agent-hero">
          <div className="container">
            <h1 className="agent-name">Agent Ecosystem</h1>
            <p className="value-prop">Explore our full suite of autonomous intelligence modules designed to revolutionize your digital workflows.</p>
          </div>
        </section>

        <section className="api-section">
          <div className="container">
            <div className="section-header">
              <h2 className="secondary-title">Available Modules</h2>
              <p className="secondary-description">Select an agent to view its detailed setup and deployment options.</p>
            </div>

            <div className="api-grid catalog-grid">
              {enrichedPackages.map((p) => (
                <Link key={p.slug} to={`/agent/${p.slug}`} className="provider-card agent-card">
                  <div className="provider-logo" style={{ color: '#ff6000' }}>
                    {p.icon}
                  </div>
                  <h3 className="provider-name">{p.name}</h3>
                  <p className="provider-info">{p.description}</p>
                  <div className="agent-price-tag">
                    <span className="price">{p.price}</span>
                    <span className="period">{p.period}</span>
                  </div>
                  <button className="deploy-button">View Setup</button>
                </Link>
              ))}
            </div>
          </div>
        </section>
        <Separator />
        <Features />
        <Separator />
        <Trust />
        <Footer />
      </div>
    );
  }

  // Fallback for invalid product IDs
  if (!product) {
    return (
      <div className="product-page error">
        <GlobalHeader />
        <div className="container" style={{ padding: '200px 0', textAlign: 'center' }}>
          <h2>Product Not Found</h2>
          <p>The product you are looking for does not exist in our ecosystem.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="agent-page">
      <GlobalHeader />

      {/* Product Hero Section */}
      <section className="agent-hero">
        <div className="container">
          <div className="agent-icon-wrap" style={{ color: '#ff6000', marginBottom: '24px' }}>
            {product.icon}
          </div>
          <h1 className="agent-name">{product.name}</h1>
          <p className="value-prop">{product.description}</p>
          <div className="pricing-info">
            <span className="amount">{product.price}</span>
            <span className="period">{product.period}</span>
          </div>

          <button className="primary-cta-button" onClick={() => document.getElementById('setup-section').scrollIntoView({ behavior: 'smooth' })}>Set up now</button>

          <div className="microcopy">
            <span>No setup required</span>
            <span className="dot">•</span>
            <span>Works instantly via Telegram</span>
            <span className="dot">•</span>
            <span>Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* Interactive Setup Wizard */}
      <section className="api-section setup-wizard" id="setup-section">
        <div className="container">
          <div className="setup-header">
            <div className="step-indicator">
              <div className={`step-dash ${setupStep >= 1 ? 'active' : ''}`}></div>
              <div className={`step-dash ${setupStep >= 2 ? 'active' : ''}`}></div>
              <div className={`step-dash ${setupStep >= 3 ? 'active' : ''}`}></div>
            </div>
            
            {setupStep === 1 ? (
              <>
                <h2 className="secondary-title">Set up your OpenClaw</h2>
                <p className="secondary-description">Choose your preferred AI provider</p>
              </>
            ) : (
              <>
                <h2 className="secondary-title">Select API Mode</h2>
                <p className="secondary-description">How do you want to power your AI?</p>
              </>
            )}
          </div>

          {setupStep === 1 && (
            <div className="provider-selection-grid">
              {providers.map((provider) => (
                <div 
                  key={provider.id} 
                  className={`selection-card provider-card-v2 ${selectedProviderId === provider.id ? 'active' : ''}`}
                  onClick={() => { 
                    setSelectedProviderId(provider.id);
                    // Special case for Ready API AI: Auto-deploy with premium access
                    if (provider.isReadyApi) {
                      setSelectedApiMode('ready_api');
                      // Trigger actual deployment logic immediately
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('trigger-readyapi-deploy'));
                      }, 50);
                    } else {
                      setSetupStep(2);
                    }
                  }}
                >
                  <div className="provider-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 2L9 9 2 12l7 3 3 7 3-7 7-3-7-3-3-7z"/>
                    </svg>
                  </div>
                  <h3 className="provider-card-name">{provider.name}</h3>
                  {provider.isReadyApi && <div className="built-in-indicator">Built-in</div>}
                  {provider.requiresOwnKey && <div className="key-indicator">{provider.hasKey ? '✓ Key Saved' : '🔑 Key Required'}</div>}
                </div>
              ))}
            </div>
          )}

          {setupStep === 2 && (
            <div className="api-mode-selection-grid">
               <div 
                  className={`selection-card mode-card ${selectedApiMode === 'own_key' ? 'active' : ''}`}
                  onClick={() => { setSelectedApiMode('own_key'); }}
                >
                  <div className="mode-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.778-7.778zM12 2l3 3M5 13l-3 3"/>
                    </svg>
                  </div>
                  <h3 className="mode-card-title">Use Your Own Key</h3>
                  <p className="mode-card-desc">Bring your own API key. You control costs directly.</p>
                </div>
            </div>
          )}

          {setupStep === 2 && selectedApiMode === 'own_key' && (
            <div className="api-key-input-section" style={{ marginTop: '24px', textAlign: 'center' }}>
              <p style={{ marginBottom: '12px', color: '#666' }}>Enter your API key for {selectedProvider?.name}</p>
              <input
                type="password"
                className="form-input"
                placeholder={`${selectedProvider?.name} API Key`}
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                style={{ maxWidth: '400px', margin: '0 auto' }}
              />
            </div>
          )}

          <div className="setup-actions">
            {setupStep > 1 && (
              <button className="back-btn" onClick={() => { 
                setSetupStep(prev => prev - 1); 
                if (setupStep === 2) setApiKeyInput('');
              }}>
                 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                   <polyline points="15 18 9 12 15 6"></polyline>
                 </svg>
                 Back
              </button>
            )}
            
            {setupStep === 1 ? (
              <button 
                className={`deploy-now-btn ${!selectedProviderId ? 'disabled' : ''}`}
                onClick={() => selectedProviderId && setSetupStep(2)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
                Deploy Now
              </button>
            ) : (
                <button 
                  className={`continue-btn ${(!selectedApiMode || isDeploying || (selectedApiMode === 'own_key' && !apiKeyInput.trim())) ? 'disabled' : ''}`}
                  onClick={async () => {
                    if (selectedApiMode === 'own_key' && apiKeyInput.trim()) {
                      setIsDeploying(true);
                      try {
                        const api = createApiClient(getAccessToken);
                        await api.post('/settings/provider-keys', { 
                          providerId: selectedProviderId, 
                          apiKey: apiKeyInput.trim() 
                        });
                        handleDeploy();
                      } catch (err) {
                        addError(err.message || 'Failed to save API key');
                        setIsDeploying(false);
                      }
                    } else if (selectedApiMode && !isDeploying) {
                      handleDeploy();
                    }
                  }}
                >
                {isDeploying ? (
                  <>
                    <div className="loading-spinner" style={{ marginRight: '8px' }} />
                    Saving & Deploying...
                  </>
                ) : (
                  <>
                    Continue to Deploy
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '8px' }}>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </>
                )}
              </button>
            )}
          </div>
          
          <div className="setup-footer-info">
            Cloud hosted • Managed for you • Cancel anytime
          </div>
        </div>
      </section>

      <Separator />
      <Features />
      <Separator />
      <Trust />
      <Footer />
    </div>
  );
};

export default AgentPage;
